<template>
    <pendo-multiselect-filter
        ref="multiselectCreatedByFilter"
        :show-apply="showApply"
        :disabled="disabled"
        :loading="loading"
        :options="options"
        :show-all-label="false"
        :multiple="grouped"
        :show-search="true"
        :value="localValue"
        :group-label-key="grouped ? 'category' : undefined"
        :group-options-key="grouped ? 'users' : undefined"
        :group-search="grouped"
        entity-name="Created by"
        :empty-label="$t('filters.createdBy.users')"
        :search-placeholder="$t('filters.createdBy.searchPlaceholder')"
        @input="setValue" />
</template>

<script>
import { PendoMultiselectFilter } from '@pendo/components';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import uniq from 'lodash/uniq';

export default {
    name: 'MultiselectCreatedByFilter',
    components: {
        PendoMultiselectFilter,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        currentUser: {
            type: Object,
            required: true,
        },
        guides: {
            type: Array,
            default: () => [],
        },
        usernameList: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Array,
            default: () => [],
        },
        idField: {
            type: String,
            default: 'username',
        },
        idOnly: {
            type: Boolean,
            default: false,
        },
        grouped: {
            type: Boolean,
            default: false,
        },
        showApply: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input'],
    computed: {
        createdByMeOption () {
            if (this.grouped) {
                return {
                    id: 'createdByMe',
                    category: this.$t('filters.createdBy.me'),
                    users: [
                        {
                            id: this.currentUser[this.idField],
                            label: this.$t('filters.createdBy.createdByMe'),
                        },
                    ],
                };
            }

            return {
                id: this.currentUser[this.idField],
                label: this.$t('filters.createdBy.createdByMe'),
            };
        },
        uniqueUsernames () {
            let usernames = [];

            if (!isEmpty(this.usernameList)) {
                usernames = this.usernameList;
            } else {
                usernames = this.guides.map((guide) => guide.createdByUser.username);
            }

            if (isEmpty(usernames)) return [];

            usernames = usernames.filter((user) => !this.isCurrentUser(user));

            const uniqueUsernames = uniq(usernames).sort((userA, userB) => {
                if (userA?.label && userB?.label) {
                    if (userA.label < userB.label) return -1;
                    if (userA.label > userB.label) return 1;
                } else {
                    if (userA < userB) return -1;
                    if (userA > userB) return 1;
                }

                return 0;
            });

            if (isObject(uniqueUsernames[0])) {
                // Add a divider between the first user and Created by me
                uniqueUsernames[0].divided = true;
            }

            return uniqueUsernames;
        },
        filteredGroupedList () {
            if (isEmpty(this.usernameList)) return [];

            return this.usernameList.map((group) => {
                return {
                    ...group,
                    users: group.users?.filter((user) => !this.isCurrentUser(user)) || [],
                };
            }).filter((group) => group.users?.length > 0);
        },
        options () {
            if (this.grouped) {
                return [this.createdByMeOption, ...this.filteredGroupedList];
            }

            return [this.createdByMeOption, ...this.uniqueUsernames];
        },
        localValue () {
            if (!this.idOnly) return this.value;

            let { options } = this;

            if (this.grouped) {
                options = options.flatMap((group) => group.users);
            }

            return options.filter((option) => this.value.includes(option[this.idField]));
        },
    },
    methods: {
        isCurrentUser (user) {
            if (isObject(user)) {
                return user[this.idField] === this.currentUser[this.idField];
            }

            return user === this.currentUser[this.idField];
        },
        setValue (value) {
            if (!this.idOnly) {
                this.$emit('input', value);
            } else {
                const prevSelectedCurrentUser = this.value.includes(this.currentUser[this.idField]);
                const currentUserSelectedOnce =
                    value.filter((option) => option[this.idField] === this.currentUser[this.idField]).length === 1;
                const uniqId = uniq(value.map((option) => option[this.idField]));

                // Unique case to check if current user is being selected or unselected as it in the list twice
                if (prevSelectedCurrentUser && currentUserSelectedOnce) {
                    this.$emit(
                        'input',
                        uniqId.filter((id) => id !== this.currentUser[this.idField]),
                    );
                } else {
                    this.$emit('input', uniqId);
                }
            }
        },
        /** @public */
        toggleMenu () {
            this.$refs.multiselectCreatedByFilter.toggleMenu();
        },
    },
};
</script>

<style lang="less" scoped>
:deep(.pendo-multiselect-option--checkbox.border-bottom::after) {
    content: '';
    position: absolute;
    display: block;
    bottom: 0;
    left: 0;
    width: 100%;
    border-bottom: 1px solid var(--gray-lighter-5);
}
</style>
